'use client';
import { useEffect, useRef } from 'react';
import { motion, useScroll, useTransform } from 'motion/react';
import Link from 'next/link';
import { useI18n, useDirection } from '@/i18n';
import { Button } from '@/components/ui/button';
import { ArrowRight, ArrowLeft } from 'lucide-react';

// Product preview / Dashboard mockup
function DashboardPreview() {
  const { t } = useI18n();
  const m = t.hero.mockup;
  return (
    <div className="relative">
      {/* Main dashboard card */}
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4, duration: 0.6 }}
        className="relative bg-card border border-border rounded-2xl shadow-xl overflow-hidden"
      >
        {/* Header bar */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-border bg-muted/30">
          <div className="flex gap-1.5">
            <div className="w-3 h-3 rounded-full bg-border" />
            <div className="w-3 h-3 rounded-full bg-border" />
            <div className="w-3 h-3 rounded-full bg-border" />
          </div>
          <div className="flex-1 flex justify-center">
            <div className="px-4 py-1 rounded-md bg-muted text-xs text-muted-foreground">
              dashboard.swishrpulse.com
            </div>
          </div>
        </div>

        {/* Dashboard content */}
        <div className="p-6 space-y-6">
          {/* Stats row */}
          <div className="grid grid-cols-3 gap-4">
            {[
              { label: m.qualityScore, value: '94.2%', trend: '+2.4%' },
              { label: m.dsatRate, value: '3.1%', trend: '-0.8%' },
              { label: m.efficiency, value: '87%', trend: '+5.2%' },
            ].map((stat, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 + i * 0.1 }}
                className="p-4 rounded-xl bg-muted/50"
              >
                <div className="text-xs text-muted-foreground mb-1">{stat.label}</div>
                <div className="text-2xl font-display font-medium text-foreground">
                  {stat.value}
                </div>
                <div className={`text-xs mt-1 ${stat.trend.startsWith('+') ? 'text-primary' : 'text-primary'}`}>
                  {stat.trend}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Chart area */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9 }}
            className="p-4 rounded-xl bg-muted/50"
          >
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-medium text-foreground">{m.performanceTrend}</span>
              <span className="text-xs text-muted-foreground">{m.last7Days}</span>
            </div>
            {/* Simplified chart bars */}
            <div className="flex items-end gap-2 h-24">
              {[65, 72, 68, 85, 78, 92, 88].map((height, i) => (
                <motion.div
                  key={i}
                  initial={{ height: 0 }}
                  animate={{ height: `${height}%` }}
                  transition={{ delay: 1 + i * 0.05, duration: 0.4 }}
                  className="flex-1 bg-primary/20 rounded-t"
                >
                  <div
                    className="w-full bg-primary rounded-t transition-all"
                    style={{ height: '30%' }}
                  />
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Agent list preview */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.1 }}
            className="space-y-2"
          >
            {[
              { name: 'Sarah Ahmed', score: 96, evals: 24 },
              { name: 'Ali Hassan', score: 91, evals: 18 },
              { name: 'Fatima Al-Rashid', score: 89, evals: 21 },
            ].map((agent, i) => (
              <div
                key={i}
                className="flex items-center gap-3 p-3 rounded-lg bg-muted/30"
              >
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="text-xs font-medium text-primary">
                    {agent.name.charAt(0)}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-foreground truncate">
                    {agent.name}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {agent.evals} {m.evaluations}
                  </div>
                </div>
                <div className="text-sm font-medium text-foreground">
                  {agent.score}%
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </motion.div>

      {/* Floating notification card */}
      <motion.div
        initial={{ opacity: 0, x: 20, y: 20 }}
        animate={{ opacity: 1, x: 0, y: 0 }}
        transition={{ delay: 1.3 }}
        className="absolute -bottom-6 -left-6 p-4 bg-card border border-border rounded-xl shadow-lg max-w-[200px]"
      >
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
            <div className="w-2 h-2 rounded-full bg-primary" />
          </div>
          <div>
            <div className="text-xs font-medium text-foreground">{m.aiAlert}</div>
            <div className="text-xs text-muted-foreground mt-0.5">
              {m.dsatRiskDetected}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Floating score card */}
      <motion.div
        initial={{ opacity: 0, x: -20, y: -20 }}
        animate={{ opacity: 1, x: 0, y: 0 }}
        transition={{ delay: 1.4 }}
        className="absolute -top-4 -right-4 p-4 bg-card border border-border rounded-xl shadow-lg"
      >
        <div className="text-xs text-muted-foreground mb-1">{m.todaysScore}</div>
        <div className="text-3xl font-display font-medium text-primary">94.2</div>
      </motion.div>
    </div>
  );
}

// Stats strip
function StatsStrip() {
  const { t } = useI18n();

  const stats = [
    { value: t.hero.stats.agents.value, label: t.hero.stats.agents.label },
    { value: t.hero.stats.evaluations.value, label: t.hero.stats.evaluations.label },
    { value: t.hero.stats.satisfaction.value, label: t.hero.stats.satisfaction.label },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.8 }}
      className="flex flex-wrap gap-8 sm:gap-12 pt-12 border-t border-border mt-12"
    >
      {stats.map((stat, i) => (
        <div key={i}>
          <div className="text-2xl sm:text-3xl font-display font-medium text-foreground">
            {stat.value}
          </div>
          <div className="text-sm text-muted-foreground mt-1">{stat.label}</div>
        </div>
      ))}
    </motion.div>
  );
}

export function Hero() {
  const { t } = useI18n();
  const { isRTL } = useDirection();
  const ArrowIcon = isRTL ? ArrowLeft : ArrowRight;

  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end start'],
  });

  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const y = useTransform(scrollYProgress, [0, 0.5], [0, 50]);

  return (
    <section
      ref={containerRef}
      className="relative min-h-screen flex items-center overflow-hidden pt-4 sm:pt-8 lg:pt-16"
    >
      {/* Subtle background pattern */}
      <div className="absolute inset-0 pattern-dots" />

      {/* Content */}
      <motion.div
        style={{ opacity, y }}
        className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24"
      >
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left - Text content */}
          <div>
            {/* Small label */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-sm font-medium text-primary tracking-wide uppercase mb-6"
            >
              {t.hero.tagline}
            </motion.p>

            {/* Main title */}
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="font-display text-[clamp(2.5rem,6vw,5rem)] leading-[1] tracking-tight text-foreground mb-6"
            >
              <span className="block">{t.hero.titleLine1}</span>
              <span className="block text-primary">{t.hero.titleLine2}</span>
              <span className="block italic font-light">{t.hero.titleLine3}</span>
            </motion.h1>

            {/* Subtitle */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="text-lg text-muted-foreground max-w-lg mb-8 leading-relaxed"
            >
              {t.hero.subtitle}
            </motion.p>

            {/* CTA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <Button
                asChild
                size="lg"
                className="h-12 px-6 text-base bg-primary hover:bg-primary/90 text-primary-foreground group"
              >
                <Link href="/request-access">
                  <span>{t.hero.cta.primary}</span>
                  <ArrowIcon className="w-4 h-4 ms-2 group-hover:translate-x-1 rtl:group-hover:-translate-x-1 transition-transform" />
                </Link>
              </Button>

              <Button
                size="lg"
                variant="outline"
                className="h-12 px-6 text-base border-border hover:bg-muted"
              >
                {t.hero.cta.secondary}
              </Button>
            </motion.div>

            {/* Stats */}
            <StatsStrip />
          </div>

          {/* Right - Dashboard preview */}
          <div className="relative lg:ps-8 hidden lg:block">
            <DashboardPreview />
          </div>
        </div>
      </motion.div>
    </section>
  );
}
